import React, {createContext, useCallback, useEffect, useMemo, useRef, useState} from "react";

const defaultState = {
  isPlaying: false,
  wantsToPlay: false,
  playlistFiles: [],
  file: null,
  audioRef: null,
}
export const AudioPlayerContext = createContext(defaultState);

export const AudioPlayerProvider = ({ children, playlist }) => {
  const audioRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [wantsToPlay, setWantsToPlay] = useState(false);
  const [playlistFiles, setPlaylistFiles] = useState([]);
  const [trackProgress, setTrackProgress] = React.useState(0);
  const [file, setFile] = useState(0);
  const [isReady, setIsReady] = React.useState(false);

  useEffect(() => {
    if (playlist && playlist.length > 0) {
      const playListWithIds = playlist.map((file, id) => ({
        ...file,
        id: id + '',
      }));
      setPlaylistFiles(playListWithIds);
      setFile(playListWithIds[0].id);

      if (audioRef.current) {
        audioRef.current.src = playListWithIds[0].src;
        audioRef.current.preload = 'auto'
        audioRef.current.load();
      }
    }
  }, [playlist, audioRef]);

  const setAudioAndReset = useCallback((fileId) => {
    if (!audioRef.current) return;

    setFile(fileId);
    setIsPlaying(false);
    setWantsToPlay(true);
    setTrackProgress(0);
    audioRef.current.currentTime = 0;
    audioRef.current.preload = 'auto'
    audioRef.current.load();
  }, [audioRef]);

  const selectedFile = useMemo(() => {
    const currentFile = playlistFiles.find(pf => pf.id === file);
    if (!currentFile) return null;
    if (audioRef.current && audioRef.current.src !== currentFile.src) {
      audioRef.current.src = currentFile.src;
      setIsReady(false);
    }

    return currentFile;
  }, [playlistFiles, file]);

  const stop = () => {
    if (!audioRef.current) return;

    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    setIsPlaying(false);
  }

  const play = () => {
    if (!audioRef.current) return;

    if (isPlaying && !wantsToPlay) {
      audioRef.current.pause();
      setIsPlaying(false);
      return;
    }

    setIsPlaying(true);
    setWantsToPlay(false);
    audioRef.current.play();
  }

  const prev = () => {
    if (!audioRef.current) return;

    const currentIndex = playlistFiles.findIndex(pf => pf.id === file);
    const prevIndex = (currentIndex - 1 + playlistFiles.length) % playlistFiles.length;

    setAudioAndReset(playlistFiles[prevIndex].id);
  }

  const next = () => {
    if (!audioRef.current) return;

    const currentIndex = playlistFiles.findIndex(pf => pf.id === file);
    const nextIndex = (currentIndex + 1) % playlistFiles.length;

    setAudioAndReset(playlistFiles[nextIndex].id);
  }

  return (
    <AudioPlayerContext.Provider value={{
      ...defaultState,
      audioRef,
      playlistFiles,
      setFile,
      selectedFile,
      isPlaying, setIsPlaying,
      trackProgress, setTrackProgress,
      isReady, setIsReady,
      wantsToPlay, setWantsToPlay, setAudioAndReset,
      play, stop, prev, next
    }}>
      {children}
    </AudioPlayerContext.Provider>
  );
}
